/**
 * CustomerAdsPricingPage — iter-299o (Jun 2026)
 *
 * Finance → Finance Settings → "التحكم برسوم إعلانات منصة العملاء".
 * Three ad types (cars / plates / numbers), each with an on/off switch and
 * a list of pricing tiers {months 1..12 → price AED}. The customer
 * platform reads GET /api/public/customer-ads-pricing.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Car, Hash, Smartphone, Plus, Trash2, Save, Loader2, Megaphone } from 'lucide-react';
import DashboardLayout from '../../components/DashboardLayout';
import { useLanguage } from '../../context/LanguageContext';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import { getAuthHeaders } from '../../utils/authHeaders';

const API = process.env.REACT_APP_BACKEND_URL;

const AD_TYPES = [
  { id: 'cars',    ar: 'إعلانات السيارات',      en: 'Car ads',           Icon: Car,        color: '#38bdf8' },
  { id: 'plates',  ar: 'إعلانات لوحات الأرقام',  en: 'Plate number ads',  Icon: Hash,       color: '#f59e0b' },
  { id: 'numbers', ar: 'إعلانات أرقام الموبايل', en: 'Mobile number ads', Icon: Smartphone, color: '#a78bfa' },
];

const MONTHS = Array.from({ length: 12 }, (_, i) => i + 1);

const monthLabel = (m, isAr) => {
  if (!isAr) return m === 1 ? '1 month' : `${m} months`;
  if (m === 1) return 'شهر واحد';
  if (m === 2) return 'شهرين';
  if (m <= 10) return `${m} أشهر`;
  return `${m} شهراً`;
};

const EMPTY = { enabled: true, tiers: [] };

export default function CustomerAdsPricingPage() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const t = (ar, en) => (isAr ? ar : en);

  const [data, setData] = useState({ cars: EMPTY, plates: EMPTY, numbers: EMPTY });
  const [active, setActive] = useState('cars');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newPrice, setNewPrice] = useState('');
  const [newMonths, setNewMonths] = useState('1');

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/api/admin/customer-ads-pricing`, { headers: getAuthHeaders() });
        setData((d) => ({ ...d, ...(r.data?.data || {}) }));
      } catch {
        toast.error(isAr ? 'تعذّر تحميل الإعدادات' : 'Failed to load settings');
      } finally {
        setLoading(false);
      }
    })();
  }, [isAr]);

  const cur = data[active] || EMPTY;
  const meta = AD_TYPES.find((a) => a.id === active);
  const usedMonths = useMemo(() => new Set((cur.tiers || []).map((x) => x.months)), [cur]);

  useEffect(() => {
    const free = MONTHS.find((m) => !usedMonths.has(m));
    setNewMonths(String(free || 1));
  }, [usedMonths]);

  const patch = (changes) => setData((d) => ({ ...d, [active]: { ...d[active], ...changes } }));

  const addTier = () => {
    const price = Number(newPrice);
    const months = Number(newMonths);
    if (!(price >= 0) || newPrice === '') {
      toast.error(t('أدخل المبلغ بالدرهم', 'Enter the amount in AED'));
      return;
    }
    if (usedMonths.has(months)) {
      toast.error(t('هذه المدة مضافة مسبقاً', 'This duration already exists'));
      return;
    }
    const tiers = [...(cur.tiers || []), { id: Math.random().toString(36).slice(2, 10), months, price_aed: price }]
      .sort((a, b) => a.months - b.months);
    patch({ tiers });
    setNewPrice('');
  };

  const updateTier = (id, price) => patch({ tiers: cur.tiers.map((x) => (x.id === id ? { ...x, price_aed: price } : x)) });
  const removeTier = (id) => patch({ tiers: cur.tiers.filter((x) => x.id !== id) });

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        enabled: !!cur.enabled,
        tiers: (cur.tiers || []).map((x) => ({ id: x.id, months: Number(x.months), price_aed: Number(x.price_aed) || 0 })),
      };
      const r = await axios.put(`${API}/api/admin/customer-ads-pricing/${active}`, payload, { headers: getAuthHeaders() });
      setData((d) => ({ ...d, [active]: r.data?.data || payload }));
      toast.success(t(`تم حفظ ${meta.ar}`, `${meta.en} saved`));
    } catch (e) {
      toast.error(e?.response?.data?.detail?.[0]?.msg || e?.response?.data?.detail || t('فشل الحفظ', 'Save failed'));
    } finally {
      setSaving(false);
    }
  };

  const fmt = (n) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });

  return (
    <DashboardLayout
      title="التحكم برسوم إعلانات منصة العملاء"
      titleEn="Customer Platform Ads Fees"
      subtitle="أسعار نشر الإعلانات حسب المدة — تسحبها منصة العملاء مباشرة"
      subtitleEn="Listing fees by duration — read live by the customer platform"
      onBack={() => navigate('/dashboard/finance/settings')}
      breadcrumb={[
        { labelAr: 'المالية', labelEn: 'Finance', path: '/dashboard/finance' },
        { labelAr: 'إعدادات القسم المالي', labelEn: 'Finance Settings', path: '/dashboard/finance/settings' },
        { labelAr: 'رسوم إعلانات منصة العملاء', labelEn: 'Customer Ads Fees', path: null },
      ]}
    >
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-testid="customer-ads-pricing-root">
        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 text-white/40 animate-spin" /></div>
        ) : (
          <>
            {/* Ad-type switch */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
              {AD_TYPES.map((a) => {
                const on = a.id === active;
                const d = data[a.id] || EMPTY;
                return (
                  <button key={a.id} onClick={() => setActive(a.id)}
                          className={`relative p-4 rounded-2xl border-2 text-start transition-all ${on ? 'bg-white/[0.05]' : 'border-white/10 bg-white/[0.02] hover:border-white/20'}`}
                          style={on ? { borderColor: a.color } : undefined}
                          data-testid={`adtype-tab-${a.id}`}>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${a.color}18`, border: `1px solid ${a.color}40` }}>
                        <a.Icon className="w-5 h-5" style={{ color: a.color }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-white font-bold text-sm">{isAr ? a.ar : a.en}</div>
                        <div className="text-[11px] text-white/50">
                          {d.enabled ? t(`${(d.tiers || []).length} باقة`, `${(d.tiers || []).length} tiers`) : t('متوقف', 'disabled')}
                        </div>
                      </div>
                    </div>
                    {!d.enabled && <span className="absolute top-2 end-2 w-2 h-2 rounded-full bg-red-400" />}
                  </button>
                );
              })}
            </div>

            {/* Editor */}
            <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 space-y-5" data-testid={`adtype-editor-${active}`}>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${meta.color}18` }}>
                    <Megaphone className="h-5 w-5" style={{ color: meta.color }} />
                  </div>
                  <div>
                    <h3 className="text-white font-bold">{isAr ? meta.ar : meta.en}</h3>
                    <p className="text-xs text-white/50">
                      {cur.enabled
                        ? t('مفعّل — منصة العملاء تعرض هذه الباقات', 'Enabled — the customer platform shows these tiers')
                        : t('متوقف — لا تُعرض باقات مدفوعة لهذا النوع', 'Disabled — no paid tiers shown for this type')}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-white/60">{t('تفعيل', 'Enabled')}</span>
                  <Switch checked={!!cur.enabled} onCheckedChange={(v) => patch({ enabled: v })} data-testid="adtype-enabled-toggle" />
                </div>
              </div>

              <div className={`${cur.enabled ? '' : 'opacity-40 pointer-events-none'} transition-opacity space-y-4`}>
                {/* Add tier */}
                <div className="rounded-xl border border-white/10 bg-black/30 p-4">
                  <div className="text-xs font-semibold text-white/70 mb-2">{t('إضافة باقة', 'Add tier')}</div>
                  <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-2">
                    <div className="flex items-center gap-2">
                      <Input type="number" min="0" step="0.01" value={newPrice} onChange={(e) => setNewPrice(e.target.value)}
                             placeholder={t('المبلغ', 'Amount')} dir="ltr" className="bg-black/40 border-white/10 text-white"
                             onKeyDown={(e) => { if (e.key === 'Enter') addTier(); }}
                             data-testid="new-tier-price" />
                      <span className="text-white/50 text-sm shrink-0">AED</span>
                    </div>
                    <select value={newMonths} onChange={(e) => setNewMonths(e.target.value)}
                            className="bg-black/40 border border-white/10 rounded-md px-3 h-10 text-sm text-white [color-scheme:dark]"
                            data-testid="new-tier-months">
                      {MONTHS.map((m) => (
                        <option key={m} value={m} disabled={usedMonths.has(m)} className="bg-[#0a0a0a]">
                          {monthLabel(m, isAr)}{usedMonths.has(m) ? ` ✓` : ''}
                        </option>
                      ))}
                    </select>
                    <Button onClick={addTier} className="bg-[#10b981] hover:bg-[#059669] text-black font-semibold" data-testid="add-tier-btn">
                      <Plus className="w-4 h-4 me-1" /> {t('إضافة', 'Add')}
                    </Button>
                  </div>
                </div>

                {/* Tier list */}
                {(cur.tiers || []).length === 0 ? (
                  <div className="p-6 text-center text-white/50 text-sm border border-dashed border-white/10 rounded-xl" data-testid="tiers-empty">
                    {t('لا توجد باقات بعد — أضف المبلغ واختر المدة.', 'No tiers yet — enter an amount and pick a duration.')}
                  </div>
                ) : (
                  <div className="rounded-xl border border-white/10 overflow-hidden" data-testid="tiers-list">
                    <table className="w-full text-sm">
                      <thead className="bg-white/[0.03] text-white/60 text-xs">
                        <tr>
                          <th className="text-start px-3 py-2">{t('المدة', 'Duration')}</th>
                          <th className="text-start px-3 py-2">{t('السعر (AED)', 'Price (AED)')}</th>
                          <th className="text-start px-3 py-2">{t('شهرياً', 'Per month')}</th>
                          <th className="px-3 py-2" />
                        </tr>
                      </thead>
                      <tbody>
                        {cur.tiers.map((x) => (
                          <tr key={x.id} className="border-t border-white/5" data-testid={`tier-row-${x.months}`}>
                            <td className="px-3 py-2 text-white font-semibold">{monthLabel(x.months, isAr)}</td>
                            <td className="px-3 py-2">
                              <div className="flex items-center gap-2 max-w-[180px]">
                                <Input type="number" min="0" step="0.01" value={x.price_aed}
                                       onChange={(e) => updateTier(x.id, e.target.value)} dir="ltr"
                                       className="bg-black/40 border-white/10 text-white h-9" data-testid={`tier-price-${x.months}`} />
                                <span className="text-white/40 text-xs">AED</span>
                              </div>
                            </td>
                            <td className="px-3 py-2 text-white/60 text-xs font-mono" dir="ltr">{fmt((Number(x.price_aed) || 0) / x.months)} AED</td>
                            <td className="px-3 py-2 text-end">
                              <button onClick={() => removeTier(x.id)} className="h-8 w-8 rounded-lg text-white/40 hover:text-red-300 hover:bg-red-500/10 inline-flex items-center justify-center"
                                      data-testid={`tier-delete-${x.months}`} title={t('حذف', 'Delete')}>
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {cur.updated_at && (
                <div className="text-[11px] text-white/40" dir="ltr">
                  {t('آخر تحديث:', 'Last updated:')} {new Date(cur.updated_at).toLocaleString()} · {cur.updated_by}
                </div>
              )}

              <div className="flex items-center justify-end gap-3 pt-2 border-t border-white/5">
                <Button onClick={save} disabled={saving} className="bg-[#10b981] hover:bg-[#059669] text-black font-semibold" data-testid="save-adtype-btn">
                  {saving ? <Loader2 className="h-3.5 w-3.5 me-1.5 animate-spin" /> : <Save className="h-3.5 w-3.5 me-1.5" />}
                  {t(`حفظ ${meta.ar}`, `Save ${meta.en}`)}
                </Button>
              </div>
            </div>

            <p className="text-[11px] text-white/40 mt-4" dir="ltr">
              Public API for the customer platform: <code className="text-sky-300">GET /api/public/customer-ads-pricing</code>
            </p>
          </>
        )}
      </section>
    </DashboardLayout>
  );
}
